import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchMessageTypesCallback } from "../types";
import { messageTypeQueries } from "../queries";
import { decodeFilterOptions } from "./filterOptions";

/**
 * Parses the persisted "message types" filter selection and prunes it to the
 * message types currently available to the user (fetched from the backend).
 *
 * The persisted selection can include types no longer available after an
 * access change — e.g. experimental types selected while the org had internal
 * access that was later revoked. Filter storage is already org+user scoped, so
 * this guards the same-user access-change case. Until the options load, the
 * persisted selection is applied as-is; once loaded, unavailable types are
 * dropped before the filter is applied.
 *
 * @param fetchMessageTypes fetches the available message-type options
 * @param criteriaValue the raw persisted filter value (a JSON-encoded array of
 *   `{ value, label }`), or undefined when nothing is selected
 */
const useShowMessageTypes = (
  fetchMessageTypes: fetchMessageTypesCallback,
  criteriaValue: string | undefined
): Array<string> => {
  const { data: messageTypeOptions } = useQuery({
    queryKey: messageTypeQueries.list(),
    queryFn: fetchMessageTypes,
    staleTime: Infinity,
  });

  const availableMessageTypes = useMemo(
    () =>
      new Set(
        (messageTypeOptions?.groups ?? []).flatMap(
          (group) => group.message_types
        )
      ),
    [messageTypeOptions]
  );

  return useMemo(() => {
    const selected = decodeFilterOptions(criteriaValue).map(
      (option) => option.value
    );
    return messageTypeOptions
      ? selected.filter((type) => availableMessageTypes.has(type))
      : selected;
  }, [criteriaValue, messageTypeOptions, availableMessageTypes]);
};

export default useShowMessageTypes;
